import React, { useState, useEffect, useRef } from "react";
import BaseButton from "../Common/BaseButton";
import { generateProductDescription } from "../../utils/Product";
import Loader from "../Common/Loader";
import { useEmailSections } from "../Context/EmailSectionContext";
import StyleControls from "./EmailModelComponents/StyleControls";
import "./EmailDetails.css";

/**
 * Base component for Email components with consistent state management and functionality
 */
const BaseEmailComponent = ({
    id, 
    data = {}, 
    onUpdate,
    componentName = "Email Component",
    componentType = "",
    styleSettings = {
        primaryColor: "#333333",
        secondaryColor: "#a1894c",
        fontFamily: "helvetica, 'helvetica neue', arial, verdana, sans-serif"
    },
    children,
    renderEditUI,
    renderViewUI,
    previewMode = false
}) => {
    console.log(`BaseEmailComponent [${componentType || componentName}] (${id}) initialized with:`, {
        id,
        data,
        styleSettings,
        previewMode
    });

    // Get context sections for section-specific styles
    const { sections } = useEmailSections();
    
    // Get section data from context (for section-specific styles)
    const sectionData = previewMode
        ? data || {}
        : sections.find(section => section.id === id) || {};
    
    // Track the complete component data
    const [componentData, setComponentData] = useState(data);
    
    // Common state for standard fields
    const [title, setTitle] = useState(data?.title || "");
    const [description, setDescription] = useState(data?.description || "");
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isComponentEditing, setIsComponentEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    
    // Keep a reference of the original data structure
    const dataRef = useRef(data);
    
    // Merge section-specific styles with parent styles
    const sectionStyleSettings = sectionData?.styleSettings || {};
    const effectiveStyleSettings = {
        ...styleSettings,  // Parent styles (defaults)
        ...sectionStyleSettings  // Section-specific overrides
    };
    
    // Extract styling values with defaults to prevent undefined errors
    const { 
        primaryColor = "#333333", 
        secondaryColor = "#a1894c", 
        backgroundColor = "#ffffff",
        fontFamily = "helvetica, 'helvetica neue', arial, verdana, sans-serif" 
    } = effectiveStyleSettings;

    // Ensure we always have a complete styleSettings object with defaults
    const completeStyleSettings = {
        primaryColor,
        secondaryColor,
        backgroundColor,
        fontFamily,
        ...effectiveStyleSettings
    };

    // Update local state when data changes from parent
    useEffect(() => {
        if (data) {
            console.log(`BaseEmailComponent [${componentType || componentName}] (${id}) useEffect data changed:`, data);
            
            // Update the complete component data
            setComponentData(data);
            
            // Update the common fields
            if (data.title !== undefined) {
                setTitle(data.title || "");
                console.log(`BaseEmailComponent (${id}) updated title:`, data.title);
            }
            if (data.description !== undefined) {
                setDescription(data.description || "");
                console.log(`BaseEmailComponent (${id}) updated description:`, data.description);
            }
            
            // Keep a reference of the original data
            dataRef.current = data;
            
            // Don't close the edit mode if this was an image upload
            if (data._isImageUpload) {
                console.log(`BaseEmailComponent (${id}) received image upload flag`);
                // Remove the flag to prevent future issues
                const cleanData = {...data};
                delete cleanData._isImageUpload;
                setComponentData(cleanData);
            }
            
            // Log data for debugging
            console.log(`BaseEmailComponent (${id}) updated with data:`, data);
        }
    }, [data, id, componentName, componentType]);

    // Apply style updates when styleSettings change
    useEffect(() => {
        // This ensures components re-render when global styles change
        if (styleSettings) {
            // Just a lightweight check to trigger re-renders without DOM manipulations
            console.log("BaseEmailComponent style settings updated:", id);
        }
    }, [styleSettings, id]);

    const handleProductRegenerate = async (section, furtherDetails) => {
        try {
            setIsLoading(true);
            const response = await generateProductDescription(
                "",  // No SKU needed for text regeneration
                section,
                furtherDetails
            );
            
            const newTitle = response?.title || title;
            const newDescription = response?.description || description;
            
            setDescription(newDescription);
            setTitle(newTitle);
            
            // Update with the new text content
            handleSaveChanges(newTitle, newDescription);
        } catch (error) {
            console.error("Text regeneration failed:", error);
            setError(error.message || "Failed to regenerate text");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveChanges = (updatedTitle, updatedDescription, additionalData = {}, isImageUpload = false) => {
        if (onUpdate) {
            console.log(`BaseEmailComponent (${id}) handleSaveChanges called with:`, {
                updatedTitle,
                updatedDescription,
                additionalData,
                isImageUpload
            });
            
            // Get current section to preserve styleSettings
            const currentSection = sections.find(section => section.id === id) || {};
            
            // Start with the current component data to preserve all properties
            const updatedData = {
                ...componentData,
                title: updatedTitle,
                description: updatedDescription,
            };
            
            // Add any additional data passed
            if (additionalData && Object.keys(additionalData).length > 0) {
                Object.assign(updatedData, additionalData);
            }
            
            // Preserve styleSettings if they exist
            if (currentSection.styleSettings) {
                updatedData.styleSettings = currentSection.styleSettings;
            }
            
            // Always ensure component_name is preserved
            if (dataRef.current.component_name && !updatedData.component_name) {
                updatedData.component_name = dataRef.current.component_name;
            }
            
            // Update local state
            setComponentData(updatedData);
            setTitle(updatedTitle);
            setDescription(updatedDescription);
            
            // Add flag to indicate this is an image upload
            if (isImageUpload) {
                updatedData._isImageUpload = true;
            }
            
            // Preserve the id if it exists
            if (id) {
                updatedData.id = id;
            }
            
            console.log(`BaseEmailComponent (${id}) saving changes:`, updatedData);
            
            // Propagate changes to parent
            onUpdate(updatedData, id);
            
            // If not an image upload, close the edit mode
            if (!isImageUpload && isComponentEditing) {
                setTimeout(() => {
                    setIsComponentEditing(false);
                }, 200);
            }
        }
    };

    const toggleComponentEdit = () => {
        const newIsComponentEditing = !isComponentEditing;
        setIsComponentEditing(newIsComponentEditing);
        setIsEditing(false);
        
        // Save changes when exiting component edit mode
        if (isComponentEditing) {
            handleSaveChanges(title, description);
        }
    };

    // Pass these props to the viewUI and editUI functions
    const uiProps = {
        title,
        setTitle,
        description,
        setDescription,
        handleSaveChanges,
        styleSettings: completeStyleSettings,
        data: componentData,  // Pass the complete component data
        // Ensure common properties are always available
        features: componentData?.features || [],
        image: componentData?.image || componentData?.img || null,
        links: componentData?.links || [],
        // Include additional props that might be needed by specific components
        previewMode: previewMode
    };

    console.log(`BaseEmailComponent [${componentType || componentName}] (${id}) rendering with uiProps:`, uiProps);

    // Generate the component name to display
    const displayName = componentType || componentName;

    return (
        <div className="" style={{ fontFamily }}>
            {!previewMode && (
                <div className="section-header">
                    <div className="section-title">{displayName}</div>
                    {!isLoading && (
                        <div className="button-group d-flex align-items-center gap-2">
                            <StyleControls 
                                id={id} 
                                componentName={componentName || componentType}
                                styleSettings={styleSettings}
                                previewMode={previewMode}
                            />
                            {isComponentEditing ? (
                                <BaseButton
                                    className="btn btn-dark"
                                    children="Done"
                                    handleClick={toggleComponentEdit}
                                />
                            ) : (
                                <>
                                    <BaseButton
                                        className="outlineprimary"
                                        children="Regenerate Text"
                                        imageSrc="/regenerate-text.svg"
                                        handleClick={() => handleProductRegenerate("benefit", "")}
                                    />
                                    <BaseButton
                                        className="outlineprimary"
                                        children="Edit"
                                        imageSrc="/edit-text.svg"
                                        handleClick={toggleComponentEdit}
                                    />
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}

            {isLoading ? (
                <div className="loading"><Loader /></div>
            ) : (previewMode || !isComponentEditing) ? (
                // View UI - for preview mode or when not editing
                typeof renderViewUI === 'function'
                    ? renderViewUI(uiProps)
                    : renderViewUI
            ) : (
                // Edit UI - only in edit mode
                typeof renderEditUI === 'function' 
                    ? renderEditUI(uiProps)
                    : renderEditUI
            )}

            {error && (
                <div className="alert alert-danger mt-2" role="alert">
                    {error}
                    <button 
                        type="button" 
                        className="btn-close float-end" 
                        aria-label="Close"
                        onClick={() => setError(null)}
                    />
                </div>
            )}
        </div>
    );
};

export default BaseEmailComponent; 